import { useCallback, useEffect, useMemo, useState } from "react";
import {
  loadAssignedComps,
  loadSaleComps,
  loadLeaseComps,
  loadRentRoll,
  unassignCompFromDeal,
  deleteSaleComp,
  deleteLeaseComp,
  deleteRentRollRow,
} from "../../supabase";
import CompLibraryPicker from "./CompLibraryPicker";
import ManualCompForm from "./ManualCompForm";

// ─── Deal Comp Set ──────────────────────────────────────────────────────────
// Section inside the DealRoom that lets a user curate the deal's comp package
// + rent roll. Three sub-tabs (Sale | Lease | Rent Roll), each supporting:
//   - Add from Library (picker modal)
//   - Add Manually (inline form)
//   - Extract from Document (runs AI extraction on a deal-attached file,
//     stages results, lets user pick what to save)
//
// Read paths:
//   - Sale tab  = assignedComps[type=sale] ∪ legacy sale_comps for this deal
//   - Lease tab = assignedComps[type=lease, source≠rent_roll] ∪ legacy lease_comps
//   - Rent Roll = deal_rent_roll rows + assignedComps[source=rent_roll]
//
// Legacy per-deal sale_comps/lease_comps rows are still rendered so no data
// is lost when we switch to the new assignment model.

function fmtMoney(n) {
  if (n == null || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtSF(n) { return n ? `${Number(n).toLocaleString()} SF` : "—"; }
function fmtPct(n) { return n != null && n !== 0 ? `${Number(n).toFixed(2)}%` : "—"; }
function fmtRate(n) { return n != null && n !== 0 ? `$${Number(n).toFixed(2)}/SF` : "—"; }

export default function DealCompSet({
  dealId,
  dealName,
  dealAddresses = [],
  dealLat,
  dealLng,
  documents = [],          // list from DealRoom's documents state
  currentUser = null,      // { name, email } from MSAL for created_by attribution
  readOnly = false,
  darkMode = false,
  onExtractFromDoc,        // (doc) => void — DealRoom owns the staging modal
  reloadSignal,            // bumped by DealRoom after extraction saves so we re-fetch
}) {
  const [activeTab, setActiveTab] = useState("sale"); // 'sale' | 'lease' | 'rent_roll'

  // Data state
  const [assigned, setAssigned] = useState([]);
  const [legacySale, setLegacySale] = useState([]);
  const [legacyLease, setLegacyLease] = useState([]);
  const [rentRoll, setRentRoll] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state — staging modal lives at DealRoom level (shared with Documents
  // section's per-doc Extract button) so it's not here.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [docPickerOpen, setDocPickerOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const [a, ls, ll, rr] = await Promise.all([
      loadAssignedComps(dealId),
      loadSaleComps(dealId),
      loadLeaseComps(dealId),
      loadRentRoll(dealId),
    ]);
    setAssigned(a);
    setLegacySale(ls || []);
    setLegacyLease(ll || []);
    setRentRoll(rr || []);
    setLoading(false);
  }, [dealId]);

  useEffect(() => { reload(); }, [reload, reloadSignal]);

  // Split assigned by type and rent-roll origin
  const assignedSale = useMemo(() => assigned.filter(c => c.type === "sale"), [assigned]);
  const assignedLease = useMemo(() => assigned.filter(c => c.type === "lease" && c.source !== "rent_roll"), [assigned]);
  const assignedRentRollComps = useMemo(() => assigned.filter(c => c.type === "lease" && c.source === "rent_roll"), [assigned]);

  // Unified row lists per tab (assigned + legacy)
  const saleRows = useMemo(() => [
    ...assignedSale.map(c => ({ ...c, _origin: "assigned" })),
    ...legacySale.map(c => ({ ...c, _origin: "legacy" })),
  ], [assignedSale, legacySale]);

  const leaseRows = useMemo(() => [
    ...assignedLease.map(c => ({ ...c, _origin: "assigned" })),
    ...legacyLease.map(c => ({ ...c, _origin: "legacy" })),
  ], [assignedLease, legacyLease]);

  // Rent roll combines dedicated table + library rows tagged as rent_roll
  const rentRollRows = useMemo(() => [
    ...rentRoll.map(r => ({ ...r, _origin: "rent_roll_table" })),
    ...assignedRentRollComps.map(r => ({
      // Map comp-shaped rent-roll-library rows into the rent-roll display shape
      id: r._assignmentId,
      _libraryCompId: r.id,
      _origin: "library_rent_roll",
      subject_address: r.address,
      tenant: r.tenant,
      tenant_industry: r.tenant_industry,
      suite_id: r.suite_id || null,
      suite_sf: r.suite_sf,
      lease_rate: r.lease_rate,
      lease_type: r.lease_type,
      lease_start: r.lease_start || r.comp_date,
      lease_end: r.lease_end,
      walt_years: r.walt_years,
      notes: r.notes,
    })),
  ], [rentRoll, assignedRentRollComps]);

  async function handleRemove(row, tab) {
    if (readOnly) return;
    if (!window.confirm("Remove this row from the deal? (The underlying comp / library row is kept; only the deal assignment is removed.)")) return;
    if (row._origin === "assigned" || row._origin === "library_rent_roll") {
      // Unassign from deal_assigned_comps
      const compId = row._libraryCompId || row.id;
      await unassignCompFromDeal(dealId, compId);
    } else if (row._origin === "legacy" && tab === "sale") {
      await deleteSaleComp(row.id);
    } else if (row._origin === "legacy" && tab === "lease") {
      await deleteLeaseComp(row.id);
    } else if (row._origin === "rent_roll_table") {
      await deleteRentRollRow(row.id);
    }
    reload();
  }

  // Already-assigned ids/keys passed to the library picker so it can flag dupes
  const alreadyAssignedIds = useMemo(() => new Set(assigned.map(c => c.id)), [assigned]);

  const cls = darkMode
    ? { panel: "bg-[rgba(44,44,44,0.6)] border-[rgba(58,58,58,0.4)]", text: "text-white", muted: "text-[#8a8a8a]", tabBg: "bg-[rgba(58,58,58,0.4)]", tabActive: "bg-pp-red text-white", rowHover: "hover:bg-[rgba(58,58,58,0.2)]", border: "border-[rgba(58,58,58,0.4)]" }
    : { panel: "bg-white border-pp-gray", text: "text-pp-dark", muted: "text-[#8a8a8a]", tabBg: "bg-pp-light", tabActive: "bg-pp-red text-white", rowHover: "hover:bg-pp-light/60", border: "border-pp-gray" };

  const currentRows = activeTab === "sale" ? saleRows : activeTab === "lease" ? leaseRows : rentRollRows;
  const counts = { sale: saleRows.length, lease: leaseRows.length, rr: rentRollRows.length };

  // Rent roll summary stats (when on RR tab)
  const rrSummary = useMemo(() => {
    if (rentRollRows.length === 0) return null;
    const totalSf = rentRollRows.reduce((s, r) => s + (Number(r.suite_sf) || 0), 0);
    const weighted = rentRollRows.reduce((s, r) => s + (Number(r.suite_sf) || 0) * (Number(r.lease_rate) || 0), 0);
    const avgRate = totalSf > 0 ? weighted / totalSf : 0;
    const walts = rentRollRows.map(r => Number(r.walt_years)).filter(n => Number.isFinite(n) && n > 0);
    const avgWalt = walts.length > 0 ? walts.reduce((s, n) => s + n, 0) / walts.length : null;
    return { totalSf, avgRate, avgWalt, count: rentRollRows.length };
  }, [rentRollRows]);

  return (
    <div className={`${cls.panel} border rounded-xl p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-[#e93529] font-bold text-sm tracking-wider`}>DEAL COMP SET</h3>
        {!readOnly && (
          <div className="relative">
            <button
              onClick={() => setDocPickerOpen(o => !o)}
              className="text-xs text-[#f04f44] hover:text-[#f04f44] font-semibold transition-colors"
              title="Extract comps + rent roll from an uploaded document"
            >
              📊 Extract from Document ▾
            </button>
            {docPickerOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDocPickerOpen(false)} />
                <div className={`absolute right-0 top-full mt-1 z-50 ${cls.panel.split(' ')[0]} border ${cls.border} rounded-lg shadow-xl py-1 min-w-[260px] max-h-[300px] overflow-y-auto`}>
                  {documents.length === 0 ? (
                    <div className={`px-3 py-2 text-xs ${cls.muted}`}>No documents uploaded yet. Upload an OM or rent roll above first.</div>
                  ) : (
                    documents.map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => { setDocPickerOpen(false); onExtractFromDoc?.(doc); }}
                        className={`w-full text-left px-3 py-2 text-xs ${cls.text} ${cls.rowHover}`}
                      >
                        {doc.file_name}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Sub-tab pills */}
      <div className={`inline-flex ${cls.tabBg} rounded-lg p-1 mb-3 gap-1`}>
        {[
          { id: "sale", label: `Sale Comps (${counts.sale})` },
          { id: "lease", label: `Lease Comps (${counts.lease})` },
          { id: "rent_roll", label: `Rent Roll (${counts.rr})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${activeTab === t.id ? cls.tabActive : `${cls.muted} hover:${cls.text}`}`}
            style={activeTab === t.id ? { color: "#ffffff" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Rent-roll summary card (only on RR tab) */}
      {activeTab === "rent_roll" && rrSummary && (
        <div className={`mb-3 px-3 py-2 rounded-lg border ${cls.border} text-xs ${cls.muted} flex flex-wrap gap-x-5 gap-y-1`}>
          <span><strong className={cls.text}>{rrSummary.count}</strong> tenants</span>
          <span><strong className={cls.text}>{fmtSF(rrSummary.totalSf)}</strong> total</span>
          {rrSummary.avgRate > 0 && <span>avg in-place <strong className={cls.text}>${rrSummary.avgRate.toFixed(2)}/SF</strong></span>}
          {rrSummary.avgWalt && <span>avg WALT <strong className={cls.text}>{rrSummary.avgWalt.toFixed(1)}y</strong></span>}
        </div>
      )}

      {/* List */}
      <div className="space-y-1">
        {loading && <p className={`text-xs ${cls.muted} italic py-3`}>Loading…</p>}
        {!loading && currentRows.length === 0 && (
          <p className={`text-xs ${cls.muted} italic py-3`}>
            {activeTab === "rent_roll"
              ? "No rent-roll rows yet. Add from Library, enter manually, or extract from a document."
              : "No comps yet. Add from Library, enter manually, or extract from a document."}
          </p>
        )}
        {!loading && currentRows.map((row, i) => (
          <CompRow
            key={row.id || `row-${i}`}
            row={row}
            tab={activeTab}
            onRemove={() => handleRemove(row, activeTab)}
            readOnly={readOnly}
            darkMode={darkMode}
            cls={cls}
          />
        ))}
      </div>

      {/* Add buttons */}
      {!readOnly && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[rgba(58,58,58,0.3)]">
          {activeTab !== "rent_roll" && (
            <button
              onClick={() => setPickerOpen(true)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${cls.border} ${cls.muted} hover:${cls.text} ${cls.rowHover} transition-colors`}
            >
              + From Library
            </button>
          )}
          <button
            onClick={() => setManualOpen(true)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${cls.border} ${cls.muted} hover:${cls.text} ${cls.rowHover} transition-colors`}
          >
            + Add Manually
          </button>
        </div>
      )}

      {/* Modals */}
      <CompLibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        dealId={dealId}
        dealName={dealName}
        dealLat={dealLat}
        dealLng={dealLng}
        type={activeTab === "sale" ? "sale" : "lease"}
        alreadyAssignedIds={alreadyAssignedIds}
        onAssigned={(count) => {
          if (count > 0) reload();
        }}
        darkMode={darkMode}
      />

      <ManualCompForm
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSaved={() => reload()}
        dealId={dealId}
        dealName={dealName}
        dealAddresses={dealAddresses}
        mode={activeTab === "rent_roll" ? "rent_roll" : activeTab}
        createdBy={currentUser?.name}
        darkMode={darkMode}
      />

    </div>
  );
}

function CompRow({ row, tab, onRemove, readOnly, darkMode, cls }) {
  const isRentRoll = tab === "rent_roll";
  const isSale = tab === "sale";
  const sourceBadge =
    row._origin === "assigned" && row.source?.startsWith("Deal Extract") ? { label: "📄 OM Extract", color: "#8a8a8a" } :
    row._origin === "assigned" && row.source === "manual_deal_entry"      ? { label: "✏️ Manual",     color: "#4f9d76" } :
    row._origin === "library_rent_roll"                                   ? { label: "📋 Library RR", color: "#b4b4b4" } :
    row._origin === "rent_roll_table"                                     ? { label: "📋 Extracted",  color: "#b4b4b4" } :
    row._origin === "assigned"                                            ? { label: "🔗 Library",    color: "#6a6a6a" } :
                                                                            { label: "✏️ Legacy",     color: "#8a8a8a" };

  return (
    <div className={`flex items-start gap-2 py-1.5 px-2 rounded ${cls.rowHover} group`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-semibold truncate">
            {isRentRoll
              ? (row.tenant + (row.suite_id ? ` · ${row.suite_id}` : ""))
              : (row.property_name || row.address?.split(",")[0])}
          </span>
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${sourceBadge.color}1a`, color: sourceBadge.color }}>
            {sourceBadge.label}
          </span>
        </div>
        {!isRentRoll && row.address && (
          <div className={`text-[10px] ${cls.muted} truncate`}>{row.address}</div>
        )}
        {isRentRoll && row.subject_address && (
          <div className={`text-[10px] ${cls.muted} truncate`}>{row.subject_address}</div>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] mt-0.5">
          {isSale && (
            <>
              {row.sale_price && <span>{fmtMoney(row.sale_price)}</span>}
              {row.building_sf && <span>{fmtSF(row.building_sf)}</span>}
              {row.price_psf && <span>${Number(row.price_psf).toFixed(0)}/SF</span>}
              {row.cap_rate && <span>{fmtPct(row.cap_rate)} cap</span>}
              {row.comp_date && <span className={cls.muted}>{row.comp_date}</span>}
            </>
          )}
          {tab === "lease" && (
            <>
              {row.tenant && <span className="font-medium">{row.tenant}</span>}
              {row.suite_sf && <span>{fmtSF(row.suite_sf)}</span>}
              {row.lease_rate && <span>{fmtRate(row.lease_rate)}</span>}
              {row.lease_type && <span className={cls.muted}>{row.lease_type}</span>}
              {row.comp_date && <span className={cls.muted}>{row.comp_date}</span>}
            </>
          )}
          {isRentRoll && (
            <>
              {row.suite_sf && <span>{fmtSF(row.suite_sf)}</span>}
              {row.lease_rate && <span>{fmtRate(row.lease_rate)}</span>}
              {row.lease_type && <span className={cls.muted}>{row.lease_type}</span>}
              {row.lease_start && <span className={cls.muted}>{row.lease_start}{row.lease_end ? ` → ${row.lease_end}` : ""}</span>}
              {row.walt_years && <span>{Number(row.walt_years).toFixed(1)}y WALT</span>}
            </>
          )}
        </div>
      </div>
      {!readOnly && (
        <button
          onClick={onRemove}
          className="text-[#8a8a8a] hover:text-[#f04f44] text-sm opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 px-1"
          title="Remove from deal"
        >
          ✕
        </button>
      )}
    </div>
  );
}
